import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { LoadingSpinner } from '../../../shared/ui';
import {
  getStakeholderApplicationsHref,
  getStakeholderSeminarHref,
  StakeholderGuardNotice,
  StakeholderShell,
  StakeholderSearchToolbar,
  useStakeholderGuard,
} from './StakeholderSeminarsPage';
import { useStakeholderSeminarApplications } from './StakeholderSeminarApplicationsPage';
import {
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
const { yowanPageContent } = require('./pageContent');

const normalizeMessage = (value = '') => String(value || '').trim();

const messageSearchText = (row = {}) => [
  row.applicantName,
  row.applicantNameKana,
  row.applicantEmail,
  row.slotParticipationLabel,
  row.messageText,
].filter(Boolean).join(' ').toLowerCase();

const StakeholderSeminarApplicationMessagesPage = ({ guardOptions }) => {
  const { eventId } = useParams();
  const content = yowanPageContent['/stakeholder/seminars/:eventId/application-messages'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const guard = useStakeholderGuard(guardOptions);
  const [searchQuery, setSearchQuery] = useState('');
  const {
    seminar,
    records,
    isLoading,
    loadError,
  } = useStakeholderSeminarApplications({
    eventId,
    guardStatus: guard.status,
    loadErrorMessage: content?.loadErrorMessage,
  });

  const messageRows = useMemo(() => records
    .map((row) => ({
      ...row,
      messageText: normalizeMessage(row.message),
    }))
    .filter((row) => row.messageText), [records]);

  const visibleRows = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    if (!normalizedQuery) {
      return messageRows;
    }

    return messageRows.filter((row) => messageSearchText(row).includes(normalizedQuery));
  }, [messageRows, searchQuery]);

  if (guard.status !== 'ready') {
    return <StakeholderGuardNotice notice={guard.notice} />;
  }

  return (
    <StakeholderShell
      activeNavHref="/stakeholder/seminars"
      routeKey="/stakeholder/seminars/:eventId/application-messages"
      currentUser={guard.currentUser}
    >
      <AdminPageHeader
        title={seminar ? `${seminar.title} 申込メッセージ` : content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link key="detail" to={getStakeholderSeminarHref(eventId, preservedDomainSearch)}>
            詳細
          </Link>,
          <Link key="applications" to={getStakeholderApplicationsHref(eventId, preservedDomainSearch)}>
            申込一覧
          </Link>,
        ]}
      />

      <StakeholderSearchToolbar
        searchPlaceholder={content.searchPlaceholder}
        searchValue={searchQuery}
        onSearchChange={(event) => setSearchQuery(event.target.value)}
        metaText={`${visibleRows.length}件表示`}
      />

      <div className="yowan-admin-resource-table-wrap c-surface-content">
        {isLoading ? <LoadingSpinner label="申込メッセージを読み込み中" showLabel /> : null}
        {!isLoading && loadError ? <p className="yowan-admin-form__message">{loadError}</p> : null}
        {!isLoading && !loadError && visibleRows.length === 0 ? (
          <p className="yowan-admin-form__message">表示できる申込メッセージはありません。</p>
        ) : null}
        {!isLoading && !loadError && visibleRows.length > 0 ? (
          <table className="yowan-admin-resource-table yowan-stakeholder-table yowan-stakeholder-table--messages">
            <thead>
              <tr>
                <th>申込者</th>
                <th>参加対象</th>
                <th>申込メッセージ</th>
                <th>申込日時</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <p className="yowan-admin-resource-table__title">{row.applicantName || '-'}</p>
                    {row.applicantEmail ? <p className="yowan-admin-resource-table__subline">{row.applicantEmail}</p> : null}
                  </td>
                  <td>{row.slotParticipationLabel || row.participationScopeLabel || '-'}</td>
                  <td>
                    <p className="yowan-stakeholder-message">{row.messageText}</p>
                  </td>
                  <td>{row.createdAt || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </div>
    </StakeholderShell>
  );
};

export default StakeholderSeminarApplicationMessagesPage;
